import { API_BASE_URL } from 'config';
import { authService } from 'services/auth.service';
import { type ApiResponse } from 'utils/types';

export interface FavouriteItem {
  favouriteId: number;
  variantId: number;
  createdAt: string;
}

interface FavouritesResponse {
  variantIds: number[];
  favourites: FavouriteItem[];
}

/**
 * Saved items. Every endpoint is customer-scoped and requires a token — the
 * backend derives the customer from it, so nothing here sends a customer id.
 */
class FavouriteService {
  private baseUrl = API_BASE_URL;

  private async authHeaders(): Promise<HeadersInit> {
    const token = await authService.getValidAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async parse<T>(response: Response): Promise<T> {
    const data: ApiResponse<T> | { success: false; error: string } = await response.json();
    if (!response.ok || !data.success) {
      const message =
        'error' in data && typeof data.error === 'string' ? data.error : 'Request failed';
      throw new Error(message);
    }
    return (data as ApiResponse<T>).data;
  }

  /** Variant IDs the customer has saved, newest first. */
  async getFavourites(): Promise<FavouritesResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/favourites`, {
      method: 'GET',
      headers: await this.authHeaders(),
    });
    return this.parse<FavouritesResponse>(response);
  }

  /** Save a variant. Safe to call when it is already saved. */
  async addFavourite(variantId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/v1/favourites`, {
      method: 'POST',
      headers: await this.authHeaders(),
      body: JSON.stringify({ variantId }),
    });
    await this.parse<{ variantId: number }>(response);
  }

  /** Remove a saved variant. */
  async removeFavourite(variantId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/v1/favourites/${variantId}`, {
      method: 'DELETE',
      headers: await this.authHeaders(),
    });
    await this.parse<{ variantId: number }>(response);
  }
}

export const favouriteService = new FavouriteService();
